import * as signalR from '@microsoft/signalr';
import { signalrInitialRetryDelay, signalrReconnectDelays } from './constants';
import { SignalrConnectionHub } from './types';
import { getApiUrl } from '@/shared/helpers';
import { getCrossdockIdFromStorage } from '@/warehouse/helpers/getCrossdockIdFromStorage';
import { sharedActions } from '@/warehouse/shared.state';
import { store } from '@/warehouse/store';
import { HubConnectionState } from '@microsoft/signalr';
// Should work without url (this const is not necessary). BUT it doesn't work in development with localhost.
const signalrHubUrl = getApiUrl();
let connectionRetryTimeoutId;
/** Joins the crossdock group. Membership is lost on every reconnect, so it has to be restored explicitly. */
const joinCurrentCrossdock = async (connection) => {
    try {
        const currentCrossdockId = getCrossdockIdFromStorage();
        if (!currentCrossdockId)
            return;
        await connection.invoke('JoinCrossdocking', currentCrossdockId);
    }
    catch (error) {
        console.error('SignalR Crossdocking hub join failed: ', error);
    }
};
/**
 * *withAutomaticReconnect* won't configure the *HubConnection* to retry initial start failures, so start failures need
 * to be handled manually.
 */
const startSignalrConnection = async (connection) => {
    // Any pending retry is superseded by this attempt, so only one retry chain is ever alive.
    clearTimeout(connectionRetryTimeoutId);
    try {
        await connection.start();
        // The socket is up — report it before joining the group, which is a separate concern
        // and is skipped when no crossdock has been selected yet.
        store.dispatch(sharedActions.setIsCrossdockHubSignalrConnectionActive(true));
        await joinCurrentCrossdock(connection);
    }
    catch (error) {
        console.error('SignalR Crossdocking hub start failed: ', error);
        connectionRetryTimeoutId = setTimeout(() => startSignalrConnection(connection), signalrInitialRetryDelay);
    }
};
/** Creates a singleton that stores all **Crossdocking hub** signalR connections. */
class Connector {
    connection;
    static instance;
    events;
    addEventListener;
    reconnect;
    constructor() {
        const options = { withCredentials: false };
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${signalrHubUrl}/${SignalrConnectionHub.Crossdocking}`, options)
            .withAutomaticReconnect(signalrReconnectDelays)
            .build();
        store.dispatch(sharedActions.setIsCrossdockHubSignalrConnectionActive(false));
        startSignalrConnection(this.connection);
        this.reconnect = () => {
            if (this.connection.state !== HubConnectionState.Connected)
                return;
            joinCurrentCrossdock(this.connection);
        };
        this.events = (onMessageReceived) => {
            this.connection.on('messageReceived', (username, message) => {
                onMessageReceived(username, message);
            });
        };
        this.addEventListener = (eventName, listener) => {
            const handler = (event) => listener(event, eventName);
            this.connection.on(eventName, handler);
            // The caller has no reference to the wrapper, so removal is only possible through this callback.
            return () => this.connection.off(eventName, handler);
        };
        this.connection.onclose((error) => {
            store.dispatch(sharedActions.setIsCrossdockHubSignalrConnectionActive(false));
            console.error('SignalR Crossdocking hub closed: ', error);
            startSignalrConnection(this.connection);
        });
        this.connection.onreconnecting(() => {
            store.dispatch(sharedActions.setIsCrossdockHubSignalrConnectionActive(false));
        });
        this.connection.onreconnected(() => {
            // `withAutomaticReconnect` restores the socket without firing `onclose`, so the connected state
            // has to be reported from here. Group membership is lost on reconnect, hence the rejoin.
            store.dispatch(sharedActions.setIsCrossdockHubSignalrConnectionActive(true));
            joinCurrentCrossdock(this.connection);
        });
    }
    // public newMessage = (messages: string) => {
    // 	this.connection.send('newMessage', 'foo', messages).then((x) => console.log('sent'));
    // };
    static getInstance() {
        // if (!Connector.instance) Connector.instance = new Connector(SignalrConnectionHub.Location);
        if (!Connector.instance)
            Connector.instance = new Connector();
        return Connector.instance;
    }
}
export const crossdockingHubSignalrConnector = Connector.getInstance;
